import { makeStyles } from '@material-ui/core';
import Profile from './Profile';
import moment from 'moment';
import { useGetForecastsQuery, usePrefetch } from 'utils/wemble-api';
import usePermissions from 'hooks/usePermissions';
import { StatusCardListOrderBy } from 'components/StatusCardList';
import { formattedDateRange, getStatusFromUser, getToday, statusBackgroundColor, statusColor, statusTitle } from 'components/Home/Forecast/utils';
import { ForecastDto, PublicUserDto, UserSearchParams } from 'utils/wemble-api.generated';
import CompetenceAnnotation from './CompetenceAnnotation';
import ExperienceAnnotation from './ExperienceAnnotation';
import { formatTimeAgo } from 'utils/helpers';

// Centralized configuration for sizing decisions based on content
// Progression: No content → Only comment → Only forecast → Both (maximum compression)
const getCardConfig = (hasComment: boolean, hasForecastComment: boolean, hasForecast: boolean, hasSecondaryForecast: boolean) => {

  return {
    smallProfile: (hasComment && hasForecast) || hasSecondaryForecast,
    smallCard: (hasComment && hasForecast) || hasSecondaryForecast,
    smallStatus: hasComment || (hasForecastComment),
  }

};


const useStyles = (commentLength: number, nameLength: number, forecastCommentLength: number, hasContent: boolean, dualComments: boolean, hasForecast: boolean) => makeStyles((theme) => ({
  root: {
    width: '100%',
    fontFamily: 'Roboto',
    userSelect: 'none',
  },
  statusCard: {
    position: 'relative',
    padding: '10px',
    borderRadius: '20px',
    height: '242px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'all 0.15s ease-in-out',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    '@media (hover: hover)': {
      '&:hover': {
        boxShadow: '4px 12px 20px rgba(0,0,0,0.04), -4px -4px 20px rgba(0,0,0,0.01)',
        cursor: 'pointer',
        transform: 'scale(1.015)',
      },
    },
    '&:active': {
      transform: 'scale(0.95) !important',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'row',
      gap: 6,
      height: 'unset',
      padding: `6px ${(commentLength > 0 || forecastCommentLength > 16) ? 10 : 18}px 6px 6px`,
      '&::before': {
        borderRadius: '40px 20px 20px 40px',
      },
      borderRadius: '40px 20px 20px 40px',
    },
  },
  statusCard__profile: {
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      minWidth: '140px',
    }
  },
  statusCard__status: {
    textAlign: 'center',
    '& p': {
      fontWeight: 500,
      lineHeight: 1.1,
      marginBottom: 8,
    },
    [theme.breakpoints.up('sm')]: {
      '& p': {
        fontSize: 14,
      }
    },
    [theme.breakpoints.down('xs')]: {
      textAlign: 'right',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-end',
      '& p': {
        fontSize: 13,
        marginBottom: 6
      }
    },
  },
  statusCard__status__expanded: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexGrow: 0,
    },
    '& p': {
      margin: 0,
    },
  },
  statusCard__avatar: {
    [theme.breakpoints.up('sm')]: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      marginTop: (hasContent ? 4 : 8) - (dualComments ? 10 : 0),
    },
  },
  profile__lastSeen: {
    position: 'absolute',
    marginTop: 4,
    marginRight: 4,
    right: 14,
    color: '#828282',
    fontSize: 11,
    fontWeight: 600,
    opacity: 0.8,
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  statusCard__container: {
    '& h3': {
      fontWeight: 600,
      margin: 0,
    },
    '& h2': {
      fontSize: 13,
      fontWeight: 400,
      color: '#828282',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
    },
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center',
      paddingTop: hasContent ? 4 : 8,
      '& h3': {
        fontSize: nameLength > 30 ? 16 : 18,
      }
    },
    [theme.breakpoints.down('xs')]: {
      marginLeft: 8,
      '& h3': {
        fontSize: 14
      },
      '& h2': {
        marginBottom: 0,
        fontSize: 12,
        justifyContent: 'flex-start',
      }
    },
  },
  statusDot: {
    width: 4,
    height: 4,
    borderRadius: '50%',
    backgroundColor: '#828282',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    }
  },
  statusBadge: {
    fontWeight: 500,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    }
  },
  status__active: {
    fontSize: 20,
    [theme.breakpoints.down('xs')]: {
      fontSize: 18,
    },
    fontWeight: 600,
  },
  status__message: {
    backgroundColor: 'white',
    borderRadius: '14px',
    padding: '8px 12px',
    fontSize: (commentLength > 50 ? (commentLength > 80 ? 11 : 12) : 13) - (dualComments ? 1.5 : 0.5) - ((hasForecast) ? 1 : 0),
    fontWeight: 500,
    color: '#333',
    margin: 0,
    display: 'block',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      padding: '6px 10px',
      fontSize: commentLength > 50 ? 10 : 11,
      width: 'fit-content',
      maxWidth: '100%',
    }
  },
  statusCard__infoCard: {
    backgroundColor: 'white',
    borderRadius: '14px',
    padding: '6px 10px',
    display: 'block',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      padding: '4px 8px',
      width: 'fit-content',
      maxWidth: '100%',
    }
  },
  statusCard__infoCard__compact: {
    padding: '4px 8px',
    [theme.breakpoints.down('xs')]: {
      padding: '3px 6px',
    }
  },
  statusCard__infoCard__status: {
    fontSize: forecastCommentLength > 20 ? 11 : 12,
    fontWeight: 500,
    display: 'block',
    [theme.breakpoints.down('xs')]: {
      fontSize: forecastCommentLength > 15 ? 9 : 10,
    }
  },
  statusCard__infoCard__dates: {
    fontSize: 10,
    color: '#828282',
    fontWeight: 400,
    marginLeft: 4,
    marginTop: -1,
    display: 'inline-block',
    [theme.breakpoints.down('xs')]: {
      fontSize: 9,
    }
  },
  forecastSplit: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  forecastLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    flexShrink: 0,
    paddingRight: forecastCommentLength > 16 ? 6 : 14,
    borderRight: '1px solid #e0e0e0',
  },
  forecastRight: {
    flex: 1,
    fontSize: forecastCommentLength > 16 ? (forecastCommentLength > 30 ? (forecastCommentLength > 60 ? 9 : 10) : 11) : 12.5,
    fontWeight: 500,
    textAlign: forecastCommentLength > 30 ? 'left' : 'center',
    color: '#333',
    lineHeight: 1.2,
    maxHeight: '36px',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      fontSize: forecastCommentLength > 30 ? (forecastCommentLength > 60 ? 8 : 9) : 10,
      maxHeight: '30px',
    }
  },

}));

const StatusCard = ({
  handleClick,
  orderOption,
  user: userProp,
  searchParams,
}: {
  user: PublicUserDto;
  handleClick: () => void;
  orderOption: StatusCardListOrderBy;
  searchParams?: UserSearchParams;
}) => {
  const prefetchUser = usePrefetch('getUserById');
  const prefetchSkills = usePrefetch('getLevelOfSkills');
  const prefetchWeeklyHours = usePrefetch('getWeeklyHours');
  const prefetchRecentMatters = usePrefetch('getRecentMatters');
  const prefetchExperienceProfile = usePrefetch('getExperienceProfile');
  const { currentUser, isAdmin, canViewHours: canPrefetchHours, canViewMatters: canPrefetchMatters, canViewExperience: showExperience } = usePermissions();

  var user = userProp;
  if (!user) return null;
  if (user.message?.message && !user.message?.message?.replaceAll(" ", "").replaceAll(".", "").length) user = { ...user, message: { ...user.message, message: "" } };

  const today = getToday()
  var comment = user.message?.message

  const { data: allForecasts } = useGetForecastsQuery({}, { refetchOnReconnect: true });
  const forecasts = allForecasts?.filter((forecast) => forecast.user == user?._id && moment(forecast.endDate) >= moment(today)).sort((a, b) => new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime()) || []

  const currentForecast = forecasts.find(forecast => moment(forecast.startDate) <= moment(today))

  // Use forecast as source of truth for status — user.onLeave can be stale due to cron timing
  const status = currentForecast?.status === 'away' ? 'away' : getStatusFromUser(user)
  const forecastHorizon = moment(today).add(30, 'days');
  const futureForecasts = forecasts.filter(forecast => moment(forecast.startDate) > moment(today) && moment(forecast.startDate) <= forecastHorizon)

  // Determine which items to display
  let forecast: ForecastDto | undefined = undefined;
  let secondaryForecast: ForecastDto | undefined = undefined;

  if (comment) {
    // General comment present: only show one forecast
    if (currentForecast?.comment) {
      forecast = currentForecast;
    } else {
      forecast = futureForecasts[0] || currentForecast;
    }
    // Never show secondary forecast if general comment exists
  } else if (currentForecast?.comment && futureForecasts.length > 0) {
    // No general comment, but both current forecast with comment and a next forecast: show both
    forecast = currentForecast;
    secondaryForecast = futureForecasts[0];
  } else {
    // Only one forecast to show
    forecast = currentForecast?.comment ? currentForecast : (futureForecasts[0] || currentForecast);
  }

  const hasContent = Boolean(comment || forecast || secondaryForecast);
  // Updated forecast comment logic to work with new forecast selection
  const forecastComment = Boolean(forecast?.comment && (forecast?.status != 'away' || forecast?.comment?.length > 18))
  const config = getCardConfig(Boolean(comment), forecastComment, Boolean(forecast || secondaryForecast), Boolean(secondaryForecast));
  const classes = useStyles(comment?.length || 0, user.name?.length || 0, forecast?.comment?.length || 0, hasContent, forecastComment && Boolean(comment), Boolean(forecast || secondaryForecast))();

  const timeAgo = user.lastUpdated ? formatTimeAgo(user.lastUpdated) : '';

  // Helper function to render a forecast item
  const renderForecast = (forecastItem: ForecastDto, key: string, isSecondary = false, showComment = true) => {
    const displayComment = showComment ? forecastItem.comment : null;
    const opacity = comment ? 0.8 : (isSecondary ? 0.7 : 1);
    const marginTop = comment ? 6 : (isSecondary ? 4 : 0);

    return (
      <div key={key} className={`${classes.statusCard__infoCard} ${config.smallCard ? classes.statusCard__infoCard__compact : ''}`}
        style={{ opacity, marginTop }}>
        {displayComment && !(forecastItem.status === 'away' && displayComment.length <= 18) ? (
          <div className={classes.forecastSplit}>
            <div className={classes.forecastLeft}>
              <div className={classes.statusCard__infoCard__status} style={{ color: statusColor(forecastItem.status) }}>
                {statusTitle(forecastItem.status)}
              </div>
              <div className={classes.statusCard__infoCard__dates}>
                {formattedDateRange(forecastItem)}
              </div>
            </div>
            <div className={classes.forecastRight}>
              {displayComment}
            </div>
          </div>
        ) : (
          <div className={classes.statusCard__infoCard__status} style={{ color: statusColor(forecastItem.status) }}>
            {displayComment && forecastItem.status === 'away' && displayComment.length <= 18
              ? displayComment
              : statusTitle(forecastItem.status)}
            <span className={classes.statusCard__infoCard__dates}>
              {`${formattedDateRange(forecastItem)}`}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={classes.root}>
        <div
          className={classes.statusCard}
          style={{
            backgroundColor: statusBackgroundColor(status),
          }}
          onClick={handleClick}
          onMouseEnter={() => {
            prefetchUser({ id: user._id! }, { ifOlderThan: 180 });
            prefetchSkills(undefined, { ifOlderThan: 1000 });
            const isOwnCard = currentUser?._id === user._id;
            if (canPrefetchHours || isOwnCard) {
              prefetchWeeklyHours({ userId: user._id!, weeks: 4 }, { ifOlderThan: 60 });
            }
            if (canPrefetchMatters || isOwnCard) {
              prefetchRecentMatters({ userId: user._id! }, { ifOlderThan: 60 });
            }
            if (showExperience) {
              prefetchExperienceProfile({ userId: user._id! }, { ifOlderThan: 60 });
            }
          }}
        >
          <div className={classes.profile__lastSeen}>
            {orderOption === 'experience'
              ? user.startYear
                ? new Date().getFullYear() - user.startYear > 0
                  ? new Date().getFullYear() - user.startYear + ' years'
                  : 'first year'
                : ''
              : user.lastUpdated && !user.onLeave && timeAgo}
          </div>

          <div className={classes.statusCard__profile}>
            <div className={classes.statusCard__avatar}>
              <Profile onLeave={Boolean(user.onLeave)} data={user} small={config.smallProfile} />
            </div>
            <div className={classes.statusCard__container}>
              <h3>{user.name}</h3>
              <h2>
                {user.title}
                {config.smallStatus && user.title && user.title.length < 24 && (
                  <>
                    <span className={classes.statusDot} />
                    <span className={classes.statusBadge} style={{ color: statusColor(status) }}>
                      {statusTitle(status)}
                    </span>
                  </>
                )}
              </h2>
            </div>
          </div>

          <div className={`${classes.statusCard__status} ${!config.smallStatus && !hasContent && classes.statusCard__status__expanded}`}>
            {!config.smallStatus && (
              <p>
                <span className={classes.status__active} style={{ color: statusColor(status) }}>
                  {statusTitle(status)}
                </span>
              </p>
            )}

            {/* Render display items based on new priority system */}
            {comment && (
              <div key="comment" className={`${classes.status__message} ${config.smallCard ? classes.statusCard__infoCard__compact : ''}`}>
                {comment}
              </div>
            )}

            {forecast && (
              renderForecast(forecast, 'forecast', false, true)
            )}

            {/* Show secondary forecast compactly (no comment) */}
            {secondaryForecast && (
              renderForecast(secondaryForecast, 'secondaryForecast', true, false)
            )}
          </div>
        </div>
      </div>
      {searchParams?.competences && searchParams.competences.length > 0 && (
        <CompetenceAnnotation user={user} competenceFilters={searchParams.competences} />
      )}
      <ExperienceAnnotation userId={user._id!} userName={user.name?.split(' ')[0]} />
    </>
  );
};

export default StatusCard;